import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { UtilityService } from '../utility/utility.service';
import { API_CONFIG } from '../../config/api.config';
import { CreditCardExpense } from '../../models/credit-card-expense.model';
import { MercadoPagoConnectUrl, MercadoPagoContact, MercadoPagoContactUpdate, MercadoPagoPendingExpense, MercadoPagoStatus, MercadoPagoSyncResult } from '../../models/mercadopago.model';

@Injectable({
  providedIn: 'root'
})
export class MercadoPagoService {

  private _utilityService = inject(UtilityService);

  private get baseUrl() {
    return `${API_CONFIG.baseUrl}${API_CONFIG.modules.mercadopago.base}`;
  }

  /**
   * Gets the Mercado Pago OAuth authorization URL to redirect the browser to.
   */
  getConnectUrl(): Observable<MercadoPagoConnectUrl> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.connect}`;
    return this._utilityService.request<MercadoPagoConnectUrl>('GET', url);
  }

  /**
   * Gets the current connection status for this user.
   */
  getStatus(): Observable<MercadoPagoStatus> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.status}`;
    return this._utilityService.request<MercadoPagoStatus>('GET', url);
  }

  /**
   * Fetches recently collected payments and imports the new ones as transactions.
   */
  sync(): Observable<MercadoPagoSyncResult> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.sync}`;
    return this._utilityService.request<MercadoPagoSyncResult>('POST', url, {});
  }

  /**
   * Disconnects the user's Mercado Pago account.
   */
  disconnect(): Observable<{ message: string }> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.disconnect}`;
    return this._utilityService.request<{ message: string }>('DELETE', url);
  }

  private _contacts = signal<MercadoPagoContact[]>([]);
  private _contactsLoaded = false;
  readonly contacts = this._contacts.asReadonly();
  private _pending = signal<MercadoPagoPendingExpense[]>([]);
  readonly pendingExpenses = this._pending.asReadonly();

  /**
   * Credit card purchases detected in Mercado Pago that still need a Fina card assigned.
   */
  getPendingExpenses(): Observable<MercadoPagoPendingExpense[]> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.pendingExpenses}`;
    return this._utilityService.request<MercadoPagoPendingExpense[]>('GET', url).pipe(
      tap(pending => this._pending.set(pending))
    );
  }

  assignPendingExpense(id: string, cardId: string): Observable<CreditCardExpense> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.assignPendingExpense(id)}`;
    return this._utilityService.request<CreditCardExpense>('POST', url, { card_id: cardId }).pipe(
      tap(() => this._removePending(id))
    );
  }

  dismissPendingExpense(id: string): Observable<{ message: string }> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.dismissPendingExpense(id)}`;
    return this._utilityService.request<{ message: string }>('DELETE', url).pipe(
      tap(() => this._removePending(id))
    );
  }

  private _removePending(id: string): void {
    this._pending.update(list => list.filter(p => p.id !== id));
  }

  /**
   * Gets the Mercado Pago account ids seen on the user's payments and caches them.
   */
  getContacts(): Observable<MercadoPagoContact[]> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.contacts}`;
    return this._utilityService.request<MercadoPagoContact[]>('GET', url).pipe(
      tap(contacts => {
        this._contacts.set(contacts);
        this._contactsLoaded = true;
      })
    );
  }

  /**
   * Loads contacts once (silently, no loader) so lists can resolve aliases. Failures are ignored.
   */
  ensureContactsLoaded(): void {
    if (this._contactsLoaded) return;
    this._contactsLoaded = true;
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.contacts}`;
    this._utilityService.request<MercadoPagoContact[]>('GET', url, {}, {}, true, { 'x-skip-loader': 'true' }).subscribe({
      next: contacts => {
        this._contacts.set(contacts);
      },
      error: () => { this._contactsLoaded = false; }
    });
  }

  updateContact(mpId: string, body: MercadoPagoContactUpdate): Observable<MercadoPagoContact> {
    const url = `${this.baseUrl}${API_CONFIG.modules.mercadopago.endpoints.updateContact(mpId)}`;
    return this._utilityService.request<MercadoPagoContact>('PUT', url, body).pipe(
      tap(updated => this._contacts.update(list =>
        list.some(c => c.mp_id === updated.mp_id)
          ? list.map(c => c.mp_id === updated.mp_id ? { ...c, ...updated } : c)
          : [...list, updated]
      ))
    );
  }

  /** Sets only the alias (partial PUT; other contact fields are untouched). */
  saveAlias(mpId: string, alias: string): Observable<MercadoPagoContact> {
    return this.updateContact(mpId, { alias });
  }

  /**
   * Contacts that have a non-empty alias, plus any ids in `keepIds` (rows the user is editing
   * whose alias was just cleared, so they don't vanish until the next load).
   */
  getAliasedContacts(keepIds: ReadonlySet<string> = new Set()): MercadoPagoContact[] {
    return this._contacts().filter(c => !!c.alias?.trim() || keepIds.has(c.mp_id));
  }

  /** Cached contact for a Mercado Pago id, if known. */
  getContact(mpId?: string | null): MercadoPagoContact | undefined {
    if (mpId) this.ensureContactsLoaded();
    return mpId ? this._contacts().find(c => c.mp_id === mpId) : undefined;
  }

  /**
   * Display name for a counterparty id: the contact alias when set, otherwise the raw id.
   */
  getContactLabel(mpId?: string | null): string | null {
    if (!mpId) return null;
    this.ensureContactsLoaded();
    return this._contacts().find(c => c.mp_id === mpId)?.alias || mpId;
  }
}
